import { inject, Injectable, Injector } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { BehaviorSubject, Observable } from "rxjs";

import {
  GeofencingDialogConfig,
  GeofencingDialogResult,
  GeofencingFilter,
  GeometryType,
  MapSelectionResult,
  SearchDisplayMode,
  SearchFilters,
} from "../../model";
// Imported by direct path (not the "../../dialog" barrel) to avoid a circular import:
// that barrel re-exports search-export-dialog.component.ts, which imports SearchQueryService
// from the "./search" barrel this file belongs to.
import { GeofencingChangeWarningDialogComponent } from "../../dialog/search-geofencing/geofencing-change-warning-dialog.component";
import { SearchGeofencingDialogComponent } from "../../dialog/search-geofencing/search-geofencing-dialog.component";
import { GeofencingService } from "../../../shared/services/geofencing.service";
import { MapActionsService } from "../../../shared/services/map-actions.service";
import { SearchQueryService } from "./search-query.service";

const GEOFENCING_DIALOG_TITLE = "Configure Geofencing Filter";
const GEOFENCING_DIALOG_DESCRIPTION =
  "Define a geographical area to filter your search results within a specific region.";

/**
 * Single, shared owner of the header's geofencing configuration flow — opening the geofencing
 * dialog, applying/removing the resulting filter, and the map-selection round trip. Both
 * `search-enhanced` (toolbar icon) and `search-settings` (settings panel button) delegate to
 * this service instead of each keeping their own copy of the same workflow.
 *
 * Also owns the confirmation shown before a change that would discard the currently displayed
 * search results (add, edit, or remove).
 */
@Injectable({
  providedIn: "root",
})
export class GeofencingSearchFilterService {
  private readonly dialog = inject(MatDialog);

  private readonly injector = inject(Injector);

  private readonly geofencingService = inject(GeofencingService);

  private readonly mapSelectionService = inject(MapActionsService);

  private readonly searchQueryService = inject(SearchQueryService);

  private readonly currentFilterSubject = new BehaviorSubject<GeofencingFilter | null>(null);

  /** Currently applied geofencing filter (wkt/location/radius), or null when none is set. */
  readonly currentFilter$: Observable<GeofencingFilter | null> = this.currentFilterSubject.asObservable();

  /** Geometry pre-filled by a map selection while the dialog is temporarily closed. */
  private pendingDialogConfig: GeofencingDialogConfig | null = null;

  /** Invoked every time the geofencing dialog closes (including intermediate map-selection round trips). */
  private onDialogClosed: (() => void) | null = null;

  constructor() {
    // Keep currentFilter$ in sync with geometries applied from outside this service (e.g. the
    // AI's extract_embeddings tool), which never go through applyGeofencingFilter().
    this.geofencingService.geofencingState$.subscribe((state) => {
      if (!state.hasGeometry) {
        this.currentFilterSubject.next(null);
      } else if (state.geometryType === "polygon" && state.displayWkt) {
        this.currentFilterSubject.next({ type: GeometryType.POLYGON, wkt: state.displayWkt });
      }
    });

    this.mapSelectionService.geofencingSelectionResult$.subscribe((result) => {
      if (result && this.pendingDialogConfig) {
        this.handleGeofencingSelectionResult(result);
      }
    });

    this.geofencingService.geofencingLayerResult$.subscribe((result) => {
      if (result && !result.success && result.error) {
        console.error("GeofencingSearchFilterService: geofencing layer operation failed", result.error);
      }
    });
  }

  /**
   * Opens the geofencing configuration dialog, pre-filled with the currently applied filter.
   * @param opts.onDialogClosed Called every time the dialog closes, including the intermediate
   * closes that happen while the user selects geometry directly on the map
   */
  openDialog(opts: { onDialogClosed?: () => void }): void {
    this.onDialogClosed = opts.onDialogClosed ?? null;
    const current = this.currentFilterSubject.value;

    this.geofencingService.getAvailableLayers$().subscribe((availableLayers) => {
      this.openDialogWith({
        title: GEOFENCING_DIALOG_TITLE,
        description: GEOFENCING_DIALOG_DESCRIPTION,
        canUseMapSelection: this.isMapDisplayMode(),
        wkt: current?.wkt,
        location: current?.location,
        radiusMeters: current?.radiusMeters,
        availableLayers,
      });
    });
  }

  /**
   * Removes the currently applied geofencing filter, after confirming with the user if there
   * are active search results that would be discarded.
   */
  removeGeofencingFilter(): void {
    this.applyOrConfirm(() => this.doRemove());
  }

  private isMapDisplayMode(): boolean {
    return this.searchQueryService.getCurrentDisplayMode() === SearchDisplayMode.MAP;
  }

  private openDialogWith(config: GeofencingDialogConfig): void {
    const dialogRef = SearchGeofencingDialogComponent.open(this.dialog, config, this.injector);
    dialogRef.afterClosed().subscribe((dialogResult: GeofencingDialogResult | undefined) => {
      this.onDialogClosed?.();
      if (dialogResult) {
        this.handleGeofencingResult(dialogResult);
      } else {
        this.pendingDialogConfig = null;
        this.mapSelectionService.requestGeofencingSelection({ id: null, type: null });
      }
    });
  }

  private handleGeofencingResult(result: GeofencingDialogResult): void {
    if (!result.success && result.delete) {
      this.pendingDialogConfig = null;
      this.removeGeofencingFilter();
    } else if (result.success) {
      this.pendingDialogConfig = null;
      this.applyOrConfirm(() => this.doApply(result));
    } else if (result.mapSelectionRequest) {
      const current = this.currentFilterSubject.value;
      this.pendingDialogConfig = {
        title: GEOFENCING_DIALOG_TITLE,
        description: GEOFENCING_DIALOG_DESCRIPTION,
        wkt: current?.wkt,
        location: current?.location,
        radiusMeters: current?.radiusMeters,
      };
      this.mapSelectionService.requestGeofencingSelection(result.mapSelectionRequest);
    }
  }

  /**
   * Handles geometry selected directly on the map and reopens the dialog with it pre-filled.
   */
  private handleGeofencingSelectionResult(result: MapSelectionResult): void {
    if (result.wkt) {
      const dialogRef = SearchGeofencingDialogComponent.open(
        this.dialog,
        { ...this.pendingDialogConfig, wkt: result.wkt, canUseMapSelection: this.isMapDisplayMode() },
        this.injector,
      );
      dialogRef.afterClosed().subscribe((dialogResult: GeofencingDialogResult | undefined) => {
        this.onDialogClosed?.();
        // Only clear pendingDialogConfig on dismissal — handleGeofencingResult() re-arms it
        // when the user starts another map selection round trip.
        if (dialogResult) {
          this.handleGeofencingResult(dialogResult);
        } else {
          this.pendingDialogConfig = null;
          this.mapSelectionService.requestGeofencingSelection({ id: null, type: null });
        }
      });
    } else {
      this.reopenGeofencingDialog();
    }
  }

  /** Reopens the dialog without geometry after a map selection was cancelled. */
  private reopenGeofencingDialog(): void {
    if (!this.pendingDialogConfig) {
      return;
    }
    const dialogRef = SearchGeofencingDialogComponent.open(
      this.dialog,
      { ...this.pendingDialogConfig, canUseMapSelection: this.isMapDisplayMode() },
      this.injector,
    );
    dialogRef.afterClosed().subscribe((dialogResult: GeofencingDialogResult | undefined) => {
      this.onDialogClosed?.();
      if (dialogResult) {
        this.handleGeofencingResult(dialogResult);
      } else {
        this.pendingDialogConfig = null;
        this.mapSelectionService.requestGeofencingSelection({ id: null, type: null });
      }
    });
  }

  /**
   * Runs `action` immediately, unless there are active search results — in that case the user
   * is asked to confirm first, since applying or removing a geofencing filter always clears
   * the current search.
   */
  private applyOrConfirm(action: () => void): void {
    if (!this.searchQueryService.hasActiveSearchResults()) {
      action();
      return;
    }
    GeofencingChangeWarningDialogComponent.open(this.dialog)
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.searchQueryService.clearSearch();
        action();
      });
  }

  private doApply(result: GeofencingDialogResult): void {
    try {
      const filters: SearchFilters = {};
      if (result.type === GeometryType.POLYGON) {
        filters.wkt = result.wkt;
        filters["buffer-meter"] = null;
      } else if (result.type === GeometryType.RADIUS) {
        filters.wkt = result.location;
        filters["buffer-meter"] = result.radiusMeters;
      }
      this.currentFilterSubject.next({
        type: result.type,
        wkt: result.wkt,
        location: result.location,
        radiusMeters: result.radiusMeters,
      });
      this.searchQueryService.updateSearchFilters(filters);
      this.geofencingService.requestCreateOrUpdateLayer(result);
      this.mapSelectionService.requestGeofencingSelection({ id: null, type: null });
    } catch (error) {
      console.error("GeofencingSearchFilterService: failed to apply geofencing filter", error);
      this.geofencingService.showError("Failed to apply geofencing filter");
    }
  }

  private doRemove(): void {
    try {
      this.geofencingService.requestRemoveLayer();
      this.currentFilterSubject.next(null);
      this.searchQueryService.updateSearchFiltersAndState({ wkt: null, "buffer-meter": null });
      this.mapSelectionService.requestGeofencingSelection({ id: null, type: null });
    } catch (error) {
      console.error("GeofencingSearchFilterService: failed to remove geofencing filter", error);
      this.currentFilterSubject.next(null);
    }
  }
}
